/**
 * USSA API Client
 * ===============
 * Client for the USSA Proxy (local or Lambda). All business logic lives here in the client.
 * The proxy is just a token injector.
 * 
 * Development: Uses localhost:3001 proxy (run: cd proxy && npm start)
 * Production: Uses Lambda proxy URL from REACT_APP_USSA_PROXY_URL
 */

// Proxy URL - localhost for dev, Lambda for production
const PROXY_URL = process.env.REACT_APP_USSA_PROXY_URL || 'http://localhost:3002/proxy';

// Direct API access (for testing only - requires token in browser, not recommended)
const DEV_MODE = process.env.REACT_APP_USSA_DEV_MODE === 'true';
const DEV_TOKEN = process.env.REACT_APP_USSA_TOKEN || '';
const USSA_API_BASE = 'https://api.usskiandsnowboard.org/v1';

/**
 * Make a request to the USSA API via the proxy Lambda.
 * 
 * @param {string} endpoint - API endpoint (e.g., 'members', 'crossCountryRankings')
 * @param {Object} options - Request options
 * @param {Object} options.query - MongoDB-style query filter
 * @param {number} options.limit - Result limit
 * @param {number} options.skip - Pagination offset
 * @param {Object} options.sort - Sort order
 * @param {string} options.method - HTTP method (default: GET)
 * @returns {Promise<Object>} API response
 */
export async function ussaRequest(endpoint, options = {}) {
  const { query, limit, skip, sort, fields, method = 'GET', body } = options;

  // Dev mode: hit API directly (local testing only)
  if (DEV_MODE && DEV_TOKEN) {
    const queryParams = new URLSearchParams();
    if (query) queryParams.set('query', JSON.stringify(query));
    if (limit !== undefined) queryParams.set('limit', limit);
    if (skip !== undefined) queryParams.set('skip', skip);
    if (sort) queryParams.set('sort', JSON.stringify(sort));
    if (fields) queryParams.set('fields', JSON.stringify(fields));

    const url = `${USSA_API_BASE}/${endpoint}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetch(url, {
      method: method,
      headers: {
        'Authorization': `Bearer ${DEV_TOKEN}`,
        'Content-Type': 'application/json',
      },
      ...(body && { body: JSON.stringify(body) }),
    });

    const data = await response.json();
    if (!response.ok || data.errors) {
      throw new Error(data.errors?.[0]?.message || `API error: ${response.status}`);
    }
    return data;
  }

  // Production mode: go through Lambda proxy
  const requestBody = {
    endpoint,
    method,
    ...(query && { query }),
    ...(limit !== undefined && { limit }),
    ...(skip !== undefined && { skip }),
    ...(sort && { sort }),
    ...(fields && { fields }),
    ...(body && { body }),
  };

  const response = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody),
  });

  const data = await response.json();

  if (!response.ok || data.errors) {
    throw new Error(data.errors?.[0]?.message || `API error: ${response.status}`);
  }

  return data;
}

// =============================================================================
// SEASON
// =============================================================================

/**
 * Get the current season year.
 * @returns {Promise<number>} Current season (e.g., 2026)
 */
export async function getCurrentSeason() {
  const data = await ussaRequest('seasons/current');
  return data.season;
}

// =============================================================================
// MEMBERS
// =============================================================================

/**
 * Get a member by USSA ID.
 * @param {number|string} memberId - USSA member ID
 * @returns {Promise<Object|null>} Member data or null if not found
 */
export async function getMemberById(memberId) {
  const data = await ussaRequest('members', {
    query: { id: Number(memberId) },
    limit: 1,
  });
  return data.members?.[0] || null;
}

/**
 * Get multiple members by USSA IDs in a single query using $in.
 * @param {Array<number|string>} memberIds - Array of USSA member IDs
 * @returns {Promise<Array>} Array of member objects
 */
export async function getMembersByIdsBatch(memberIds) {
  if (!memberIds || memberIds.length === 0) return [];
  
  const ids = memberIds.map(id => Number(id));
  const data = await ussaRequest('members', {
    query: { id: { $in: ids } },
    limit: ids.length,
  });
  return data.members || [];
}

/**
 * Search members by name.
 * @param {string} firstName - First name
 * @param {string} lastName - Last name
 * @param {number} limit - Max results (default: 10)
 * @returns {Promise<Array>} Matching members
 */
export async function searchMembersByName(firstName, lastName, limit = 10) {
  const query = {};
  if (firstName) query.firstName = firstName;
  if (lastName) query.lastName = lastName;

  const data = await ussaRequest('members', { query, limit });
  return data.members || [];
}

/**
 * Generic search members function.
 * @param {Object} params - Search parameters
 * @param {string} params.firstName - First name
 * @param {string} params.lastName - Last name
 * @param {number} params.limit - Max results (default: 10)
 * @returns {Promise<Array>} Matching members
 */
export async function searchMembers(params = {}) {
  const { firstName, lastName, limit = 10 } = params;
  const query = {};
  
  if (firstName) {
    query.firstName = firstName.includes('*') ? { $wildcard: firstName } : firstName;
  }
  if (lastName) {
    query.lastName = lastName.includes('*') ? { $wildcard: lastName } : lastName;
  }

  const data = await ussaRequest('members', { query, limit });
  return data.members || [];
}

/**
 * Search members by name with wildcards.
 * @param {string} firstName - First name (can include * for wildcard)
 * @param {string} lastName - Last name (can include * for wildcard)
 * @param {number} limit - Max results (default: 10)
 * @returns {Promise<Array>} Matching members
 */
export async function searchMembersWildcard(firstName, lastName, limit = 10) {
  const query = {};
  if (firstName) {
    query.firstName = firstName.includes('*') ? { $wildcard: firstName } : firstName;
  }
  if (lastName) {
    query.lastName = lastName.includes('*') ? { $wildcard: lastName } : lastName;
  }

  const data = await ussaRequest('members', { query, limit });
  return data.members || [];
}

/**
 * Check if a member is current (has valid membership for season).
 * @param {Object} member - Member object from API
 * @param {number} season - Season to check (default: current)
 * @returns {boolean} True if member has valid membership
 */
export function isMemberCurrent(member, season) {
  if (!member?.memberships) return false;
  
  return member.memberships.some(
    (m) => m.season === season && m.status === 'valid'
  );
}

/**
 * Get multiple members by IDs (parallel requests).
 * @param {Array<number|string>} memberIds - Array of USSA member IDs
 * @returns {Promise<Map<string, Object>>} Map of memberId -> member data
 */
export async function getMembersByIds(memberIds) {
  const results = new Map();
  
  // Parallel requests with error handling per-member
  const promises = memberIds.map(async (id) => {
    try {
      const member = await getMemberById(id);
      if (member) results.set(String(id), member);
    } catch (err) {
      console.warn(`Failed to fetch member ${id}:`, err.message);
    }
  });

  await Promise.all(promises);
  return results;
}

// =============================================================================
// RANKINGS (POINTS)
// =============================================================================

/**
 * Get rankings for a member in a specific list.
 * @param {number|string} memberId - USSA member ID
 * @param {string} listCode - List code (e.g., '26LIST04')
 * @param {number} season - Season year
 * @returns {Promise<Object|null>} Ranking data or null
 */
export async function getMemberRanking(memberId, listCode, season) {
  const data = await ussaRequest('crossCountryRankings', {
    query: {
      memberId: Number(memberId),
      'list.code': listCode,
      season,
    },
    limit: 1,
  });
  return data.crossCountryRankings?.[0] || null;
}

/**
 * Get rankings for multiple members in a single query using $in.
 * @param {Array<number|string>} memberIds - Array of USSA member IDs
 * @param {string} listCode - List code (e.g., '26LIST04')
 * @param {number} season - Season year
 * @returns {Promise<Array>} Array of ranking objects
 */
export async function getMembersRankingsBatch(memberIds, listCode, season) {
  if (!memberIds || memberIds.length === 0) return [];
  
  const ids = memberIds.map(id => Number(id));
  const data = await ussaRequest('crossCountryRankings', {
    query: {
      memberId: { $in: ids },
      'list.code': listCode,
      season,
    },
    limit: ids.length,
  });
  return data.crossCountryRankings || [];
}

/**
 * Get rankings for multiple members (parallel requests).
 * @param {Array<number|string>} memberIds - Array of USSA member IDs
 * @param {string} listCode - List code
 * @param {number} season - Season year
 * @returns {Promise<Map<string, Object>>} Map of memberId -> ranking data
 */
export async function getMembersRankings(memberIds, listCode, season) {
  const results = new Map();

  const promises = memberIds.map(async (id) => {
    try {
      const ranking = await getMemberRanking(id, listCode, season);
      if (ranking) results.set(String(id), ranking);
    } catch (err) {
      console.warn(`Failed to fetch ranking for ${id}:`, err.message);
    }
  });

  await Promise.all(promises);
  return results;
}

/**
 * Get all rankings for a list (paginated).
 * @param {string} listCode - List code
 * @param {number} season - Season year
 * @param {number} limit - Results per page
 * @param {number} skip - Offset
 * @returns {Promise<Array>} Rankings
 */
export async function getRankingsByList(listCode, season, limit = 1000, skip = 0) {
  const data = await ussaRequest('crossCountryRankings', {
    query: { 'list.code': listCode, season },
    limit,
    skip,
  });
  return data.crossCountryRankings || [];
}

// =============================================================================
// EVENTS
// =============================================================================

/**
 * N-codex races are not eligible for NRL scoring.
 * Exclude events where every race code starts with "N".
 */
function isNrlScorableEvent(event) {
  const races = event?.races;
  if (!Array.isArray(races) || races.length === 0) return true;

  return races.some((race) => {
    const code = String(race?.code || '').trim().toUpperCase();
    if (!code) return true; // Keep if code is missing/unknown.
    return !code.startsWith('N');
  });
}

/**
 * Search events by name and sport.
 * @param {string} name - Event name (supports wildcards with *)
 * @param {number} season - Season year
 * @param {number} limit - Max results
 * @param {string} sport - Sport filter (e.g., 'cross-country', 'alpine', 'para-cross-country')
 * @returns {Promise<Array>} Matching events
 */
export async function searchEvents(name, season, limit = 10, sport = null) {
  const query = { season };
  if (name) {
    query.name = name.includes('*') ? { $wildcard: name } : name;
  }
  if (sport) {
    query.sport = sport;
  }

  const data = await ussaRequest('events', { query, limit });
  return (data.events || []).filter(isNrlScorableEvent);
}

/**
 * Get recent events (past week).
 * @param {number} season - Season year
 * @param {string} sport - Sport filter
 * @param {number} daysBack - Number of days to look back (default 7)
 * @returns {Promise<Array>} Recent events
 */
export async function getRecentEvents(season, sport = null, daysBack = 7) {
  const now = new Date();
  const pastDate = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);
  
  // Format dates as YYYY-MM-DD
  const startDate = pastDate.toISOString().slice(0, 10);
  const endDate = now.toISOString().slice(0, 10);
  
  const query = { 
    season,
    // Query for events where start date is within the past week
    'date.start': { 
      $gte: startDate,
      $lte: endDate,
    }
  };
  
  if (sport) {
    query.sport = sport;
  }

  const data = await ussaRequest('events', { 
    query, 
    limit: 50,
    sort: { 'date.start': -1 } // Most recent first
  });
  return (data.events || []).filter(isNrlScorableEvent);
}

/**
 * Map sector code to API sport value.
 * @param {string} sectorCode - Sector code (CC, PCC, PAL, PSB)
 * @returns {string} API sport value
 */
export function sectorToSport(sectorCode) {
  const mapping = {
    'CC': 'cross-country',
    'PCC': 'para-cross-country',
    'PAL': 'para-alpine',
    'PSB': 'para-snowboard',
    // Add more as needed
  };
  return mapping[sectorCode] || 'cross-country';
}

/**
 * Get event by code.
 * @param {number} eventCode - Event code
 * @param {number} season - Season year (optional, improves lookup)
 * @returns {Promise<Object|null>} Event data or null
 */
export async function getEventByCode(eventCode, season) {
  const query = { code: eventCode };
  if (season) query.season = season;

  const data = await ussaRequest('events', { query, limit: 1 });
  const event = data.events?.[0] || null;
  return event && isNrlScorableEvent(event) ? event : null;
}

/**
 * Get races for an event.
 * @param {number} eventCode - Event code
 * @param {number} season - Season year
 * @returns {Promise<Array>} Races in the event
 */
export async function getEventRaces(eventCode, season) {
  const event = await getEventByCode(eventCode, season);
  return event?.races || [];
}

// =============================================================================
// UTILITY
// =============================================================================

/**
 * Extract points from ranking for a discipline.
 * @param {Object} ranking - Ranking object
 * @param {string} discipline - 'sprint', 'distance', or 'overall'
 * @returns {number|null} Points or null
 */
export function getPoints(ranking, discipline = 'overall') {
  return ranking?.disciplines?.[discipline]?.points ?? null;
}

/**
 * Format member for display.
 * @param {Object} member - Member object
 * @returns {string} Formatted string
 */
export function formatMember(member) {
  if (!member) return '';
  return `${member.firstName} ${member.lastName} (${member.division || 'N/A'})`;
}

